import { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Modal, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { archiveNotifications, getUserNotifications, unarchiveNotifications } from '@/api/notifications.api';
import PageContainer from '@/components/Container/PageContainer';
import NotificationCard from '@/components/Notification/Cards';
import { Close, Search } from '@/components/Icon';
import { Button } from '@/components/UI/Button';
import InnerPageTitle from '@/components/UI/InnerPageTitle';
import Tabs from '@/components/UI/Tabs';
import TableStatus from '@/components/UI/TableStatus';
import useTableRequestState from '@/hooks/useTableRequestState';
import { Box, Flex } from '@/layout';
import { getAuthSelector } from '@/redux/auth/auth.slice';
import { deleteNotification, getNotification, getNotificationSelector } from '@/redux/notification/notification.slice';
import { useAppDispatch, useAppSelector } from '@/redux/store.hook';
import { errorHandler, successHandler } from '@/utils/toastHandler';
import {
  CardWrapper,
  ContentWrapper,
  SearchBar,
  SearchIconWrapper,
  ModalForm,
  ModalTitle,
  NotificationActionButton,
} from './style';

const Notifications = () => {
  // @ts-ignore -- repository translation inference exceeds TypeScript's instantiation depth.
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isLogin } = useAppSelector(getAuthSelector);
  const { notify } = useAppSelector(getNotificationSelector);
  const [cursor, setCursor] = useState<string | null>(null);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [cursorHistory, setCursorHistory] = useState<(string | null)[]>([]);
  const [archived, setArchived] = useState(false);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [multiSelect, setMultiSelect] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [submittedSearch, setSubmittedSearch] = useState('');
  const notificationRequest = useTableRequestState(notify.length > 0);
  const { control, watch, handleSubmit } = useForm<{ key: string }>({ defaultValues: { key: '' } });
  const search = watch('key');

  const load = useCallback(
    async (requestedCursor: string | null, query = submittedSearch) => {
      const normalizedQuery = query.trim();
      if (normalizedQuery.length === 1) return;
      const requestId = notificationRequest.beginRequest();
      setSelectedIds([]);
      setShowModal(false);
      try {
        const response = await getUserNotifications({ archived, q: normalizedQuery, cursor: requestedCursor });
        if (!notificationRequest.completeRequest(requestId)) return;
        dispatch(getNotification(response.data.data));
        setCursor(requestedCursor);
        setNextCursor(response.data.data.next_cursor || null);
      } catch {
        const message = t('error.server');
        if (notificationRequest.failRequest(requestId, message)) errorHandler(message);
      }
    },
    [
      archived,
      dispatch,
      notificationRequest.beginRequest,
      notificationRequest.completeRequest,
      notificationRequest.failRequest,
      submittedSearch,
      t,
    ]
  );

  useEffect(() => {
    if (!isLogin) {
      errorHandler(t('error.login'));
      navigate('/auth/signin');
      return;
    }
    setCursorHistory([]);
    load(null);
  }, [archived, isLogin, load, navigate, notificationRequest.retryKey, t]);

  const submitSearch = () => {
    const query = search.trim();
    if (query.length === 1) return;
    setCursorHistory([]);
    if (query === submittedSearch) load(null, query);
    else setSubmittedSearch(query);
  };

  const isBusy =
    notificationRequest.requestState.status === 'loading' || notificationRequest.requestState.status === 'refreshing';

  const changeArchiveState = async (ids: number[]) => {
    ids = ids.filter((id) => notify.some((item) => item.id === id));
    if (ids.length === 0) return;
    try {
      if (archived) await unarchiveNotifications(ids);
      else await archiveNotifications(ids);
      dispatch(deleteNotification(ids));
      successHandler(
        archived ? t('pages.home.notifications.restoredSuccess') : t('pages.home.notifications.archivedSuccess')
      );
      setSelectedIds([]);
      setMultiSelect(false);
      setShowModal(false);
      await load(cursor);
    } catch {
      errorHandler(t('error.server'));
    }
  };

  const tabs: TabsType = [
    {
      label: t('pages.home.notifications.activeTab'),
      index: 1,
      Component: () => null,
    },
    {
      label: t('pages.home.notifications.archivedTab'),
      index: 2,
      Component: () => null,
    },
  ];

  const selectedTab = archived ? 2 : 1;

  return (
    <PageContainer title={<InnerPageTitle link="/home" subtitle={t('notifications')} />}>
      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        aria-labelledby="notification-archive-title"
        disableEnforceFocus
      >
        <ModalForm
          role="dialog"
          aria-modal="true"
          aria-labelledby="notification-archive-title"
          onSubmit={handleSubmit(() => changeArchiveState(selectedIds))}
        >
          <Close.ButtonClose type="button" aria-label={t('buttons.cancel')} onClick={() => setShowModal(false)}>
            <Close.CloseSVG aria-hidden="true" focusable="false" />
          </Close.ButtonClose>
          <ModalTitle id="notification-archive-title">
            {archived
              ? t('pages.home.notifications.modal.restoreTitle')
              : t('pages.home.notifications.modal.archiveTitle')}
          </ModalTitle>
          <Box style={{ marginTop: '50px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Button type="submit" size="lg" variant="primary" rounded={false} marginRight={40}>
              {t('buttons.ok')}
            </Button>
            <Button
              type="button"
              size="lg"
              variant="primary-invert"
              color="primary"
              onClick={() => setShowModal(false)}
            >
              {t('buttons.cancel')}
            </Button>
          </Box>
        </ModalForm>
      </Modal>
      <CardWrapper>
        <Flex justifyContent="space-between" alignItems="center">
          <SearchBar onSubmit={handleSubmit(submitSearch)}>
            <Controller
              control={control}
              name="key"
              render={({ field }) => (
                <div style={{ width: '100%' }}>
                  <TextField
                    {...field}
                    variant="outlined"
                    sx={{ width: '100%', background: '#FFF', borderRadius: '5px 0 0 5px' }}
                    size="small"
                    autoComplete="off"
                    disabled={isBusy}
                    helperText={search.length === 1 ? t('pages.home.notifications.searchMinLength') : undefined}
                    inputProps={{ 'aria-label': t('pages.home.notifications.searchLabel'), minLength: 2, maxLength: 100 }}
                  />
                  <SearchIconWrapper
                    type="submit"
                    aria-label={t('pages.home.notifications.searchButton')}
                    disabled={isBusy}
                  >
                    <img width={32} src={Search.SearchSVG_B} alt="" />
                  </SearchIconWrapper>
                </div>
              )}
            />
          </SearchBar>
          {!multiSelect ? (
            <NotificationActionButton type="button" onClick={() => setMultiSelect(true)} disabled={isBusy}>
              {t('buttons.selectMultiple')}
            </NotificationActionButton>
          ) : (
            <Flex gridGap={20}>
              <NotificationActionButton
                type="button"
                disabled={isBusy}
                onClick={() =>
                  setSelectedIds(selectedIds.length === notify.length ? [] : notify.map((item) => item.id))
                }
              >
                {selectedIds.length === notify.length ? t('buttons.unSelectAll') : t('buttons.selectAll')}
              </NotificationActionButton>
              <NotificationActionButton
                type="button"
                disabled={isBusy || selectedIds.length === 0}
                onClick={() => setShowModal(true)}
              >
                {archived
                  ? t('pages.home.notifications.restoreSelected')
                  : t('pages.home.notifications.archiveSelected')}
              </NotificationActionButton>
              <NotificationActionButton
                type="button"
                disabled={isBusy}
                onClick={() => {
                  setMultiSelect(false);
                  setSelectedIds([]);
                }}
              >
                {t('buttons.cancel')}
              </NotificationActionButton>
            </Flex>
          )}
        </Flex>
        <Tabs
          tabs={tabs}
          selectedTab={selectedTab}
          onClick={(index) => {
            if (!isBusy) {
              setArchived(index === 2);
              setSelectedIds([]);
            }
          }}
        />
        {notificationRequest.requestState.status === 'refreshing' && notify.length > 0 ? (
          <TableStatus status="refreshing" message={t('table.status.refreshing')} compact />
        ) : null}
        {notificationRequest.requestState.status === 'error' && notificationRequest.requestState.hasStaleData ? (
          <TableStatus
            status="error"
            message={t('table.status.error')}
            retryLabel={t('table.actions.retry')}
            onRetry={notificationRequest.retry}
            compact
          />
        ) : null}
        {notificationRequest.requestState.status === 'loading' ||
        (notificationRequest.requestState.status === 'refreshing' && notify.length === 0) ? (
          <ContentWrapper>
            <TableStatus status="loading" message={t('table.status.loading')} />
          </ContentWrapper>
        ) : notificationRequest.requestState.status === 'error' && !notificationRequest.requestState.hasStaleData ? (
          <ContentWrapper>
            <TableStatus
              status="error"
              message={t('table.status.error')}
              retryLabel={t('table.actions.retry')}
              onRetry={notificationRequest.retry}
            />
          </ContentWrapper>
        ) : notify.length > 0 ? (
          <Flex width="100%" flexDirection="column" gridGap="15px" aria-busy={isBusy}>
            <NotificationCard
              data={notify}
              showSelect={multiSelect}
              selectedIds={selectedIds}
              archived={archived}
              onSelectionChange={setSelectedIds}
              onArchive={changeArchiveState}
            />
          </Flex>
        ) : (
          <ContentWrapper>
            <span>{t('pages.home.notifications.noData')}</span>
          </ContentWrapper>
        )}
        {(cursorHistory.length > 0 || nextCursor) && (
          <Flex justifyContent="center" marginTop={20}>
            <Button type="button" disabled={isBusy || cursorHistory.length === 0} onClick={() => {
              const history = [...cursorHistory];
              const previous = history.pop() ?? null;
              setCursorHistory(history);
              load(previous);
            }}>{t('buttons.previous')}</Button>
            <Button type="button" disabled={isBusy || !nextCursor} marginLeft={15} onClick={() => {
              setCursorHistory((history) => [...history, cursor]);
              load(nextCursor);
            }}>{t('buttons.next')}</Button>
          </Flex>
        )}
      </CardWrapper>
    </PageContainer>
  );
};

export default Notifications;
